import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  Alert,
  Switch,
  Modal,
  TouchableOpacity,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { Profile, UserRoleType } from "@/types";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { logRoleChange, logUserAction } from "@/lib/activity-log";
import { createInAppNotification } from "@/lib/notifications";
import { ADMIN_ROLE_NOTIFICATION_LOTTIE } from "@/modules/onboarding";
import type { PendingMemberWithRelations } from "@/modules/pending-members";

/**
 * User role record
 */
interface UserRole {
  id: string;
  role: UserRoleType;
  weekend_id: string | null;
  assigned_at: string;
  revoked_at: string | null;
}

/**
 * User with roles
 */
interface UserDetail extends Profile {
  user_roles: UserRole[];
}

/**
 * Action button component
 */
interface ActionButtonProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
  onPress: () => void;
  variant?: "default" | "danger";
  disabled?: boolean;
  colors: ReturnType<typeof useThemeColors>;
}

const ActionButton = ({
  icon,
  title,
  description,
  onPress,
  variant = "default",
  disabled = false,
  colors,
}: ActionButtonProps) => {
  const bgColor = variant === "danger" ? "#FEF2F2" : colors.surface;
  const iconColor = variant === "danger" ? "#DC2626" : "#7C3AED";
  const titleColor = variant === "danger" ? "#B91C1C" : colors.text;

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      className={`px-4 py-3 flex-row items-center active:opacity-80 ${
        disabled ? "opacity-50" : ""
      }`}
      style={{
        backgroundColor: bgColor,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
      }}
      accessibilityRole="button"
      accessibilityLabel={title}
    >
      <View
        className="w-10 h-10 rounded-full items-center justify-center mr-3"
        style={{ backgroundColor: colors.background }}
      >
        <Ionicons name={icon} size={20} color={iconColor} />
      </View>
      <View className="flex-1">
        <Text className="text-base font-semibold" style={{ color: titleColor }}>
          {title}
        </Text>
        <Text className="text-sm" style={{ color: colors.textSecondary }}>
          {description}
        </Text>
      </View>
      <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
    </Pressable>
  );
};

/**
 * Role toggle component
 */
interface RoleToggleProps {
  role: UserRoleType;
  label: string;
  description: string;
  isActive: boolean;
  onToggle: (enabled: boolean) => void;
  disabled?: boolean;
  colors: ReturnType<typeof useThemeColors>;
}

const RoleToggle = ({
  role,
  label,
  description,
  isActive,
  onToggle,
  disabled = false,
  colors,
}: RoleToggleProps) => {
  const roleColors: Record<UserRoleType, string> = {
    super_admin: "#DC2626",
    familie_admin: "#7C3AED",
    user: "#6B7280",
  };

  return (
    <View
      className="px-4 py-3 flex-row items-center"
      style={{
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
      }}
    >
      <View
        className="w-10 h-10 rounded-full items-center justify-center mr-3"
        style={{ backgroundColor: `${roleColors[role]}15` }}
      >
        <Ionicons
          name={role === "super_admin" ? "shield" : role === "familie_admin" ? "key" : "person"}
          size={20}
          color={roleColors[role]}
        />
      </View>
      <View className="flex-1">
        <Text className="text-base font-semibold" style={{ color: colors.text }}>
          {label}
        </Text>
        <Text className="text-sm" style={{ color: colors.textSecondary }}>
          {description}
        </Text>
      </View>
      <Switch
        value={isActive}
        onValueChange={onToggle}
        disabled={disabled}
        trackColor={{ false: colors.border, true: roleColors[role] }}
        thumbColor="#FFFFFF"
      />
    </View>
  );
};

/**
 * User detail screen
 * Shows user info and allows role management + admin actions
 */
export default function UserDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { user: currentUser } = useAuth();
  const colors = useThemeColors();

  const [user, setUser] = useState<UserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Pending member linking state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [unclaimedPendingMembers, setUnclaimedPendingMembers] = useState<PendingMemberWithRelations[]>([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  // Check if this is the current user (can't modify own roles)
  const isCurrentUser = currentUser?.id === id;

  /**
   * Fetch user details with roles
   */
  const fetchUser = useCallback(async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select(`
          *,
          user_roles!user_roles_user_id_fkey (
            id,
            role,
            weekend_id,
            assigned_at,
            revoked_at
          )
        `)
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching user:", error);
        Alert.alert("Fout", "Kon gebruiker niet laden");
        router.back();
        return;
      }

      setUser(data);
    } catch (error) {
      console.error("Error fetching user:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id, router]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /**
   * Fetch unclaimed pending members for linking
   */
  const fetchUnclaimedPendingMembers = useCallback(async () => {
    setIsLoadingPending(true);
    try {
      const { data, error } = await supabase
        .from("pending_members")
        .select(`
          *,
          team:teams(id, name, color)
        `)
        .is("claimed_at", null)
        .order("full_name");

      if (error) throw error;
      setUnclaimedPendingMembers(data || []);
    } catch (error) {
      console.error("Error fetching pending members:", error);
    } finally {
      setIsLoadingPending(false);
    }
  }, []);

  /**
   * Show the link modal
   */
  const handleShowLinkModal = async () => {
    await fetchUnclaimedPendingMembers();
    setShowLinkModal(true);
  };

  /**
   * Link user to a pending member
   */
  const handleLinkToPendingMember = async (pendingMemberId: string) => {
    if (!user) return;

    setIsLinking(true);
    try {
      const { error } = await supabase.rpc("link_user_to_pending_member", {
        p_user_id: user.id,
        p_pending_member_id: pendingMemberId,
      });

      if (error) throw error;

      Alert.alert(
        "✅ Gekoppeld",
        "Gebruiker is succesvol gekoppeld aan de pre-registratie",
        [{ text: "OK" }]
      );

      setShowLinkModal(false);
      await fetchUser(); // Refresh user data
    } catch (error: any) {
      console.error("Error linking user:", error);
      Alert.alert("Fout", error.message || "Kon gebruiker niet koppelen");
    } finally {
      setIsLinking(false);
    }
  };

  /**
   * Check if user has a specific active role
   */
  const hasRole = (role: UserRoleType): boolean => {
    if (!user) return false;
    return user.user_roles.some((r) => r.role === role && !r.revoked_at);
  };

  /**
   * Get human-readable role label
   */
  const getRoleLabel = (role: UserRoleType): string => {
    const labels: Record<UserRoleType, string> = {
      super_admin: "Super Admin",
      familie_admin: "Familie Admin",
      user: "Gebruiker",
    };
    return labels[role];
  };

  /**
   * Execute the role change
   */
  const executeRoleChange = async (role: UserRoleType, enabled: boolean) => {
    if (!user) return;

    setIsSaving(true);

    try {
      if (enabled) {
        // Add role
        const { error } = await supabase.from("user_roles").insert({
          user_id: user.id,
          role,
          weekend_id: null,
          assigned_by: currentUser?.id,
        });

        if (error) throw error;

        // Log role assignment
        if (currentUser) {
          logRoleChange(
            currentUser.id,
            user.id,
            user.full_name || user.email || "Onbekend",
            role,
            "assign"
          );
        }

        // Send notification for familie_admin role assignment
        if (role === "familie_admin") {
          try {
            await createInAppNotification({
              userId: user.id,
              title: "🎉 Je bent nu Familie Admin!",
              body: "Je bent toegevoegd aan de familie commissie. Bekijk de handleiding om aan de slag te gaan.",
              type: "role_assignment",
              actionUrl: "/(admin)/onboarding",
              actionData: {
                action_button_text: "Bekijk handleiding",
                role: "familie_admin",
              },
              senderId: currentUser?.id,
              lottieAnimation: ADMIN_ROLE_NOTIFICATION_LOTTIE,
            });
          } catch (notifyError) {
            // Don't fail the role change if notification fails
            console.error("Error sending admin role notification:", notifyError);
          }
        }
      } else {
        // Revoke role (soft delete)
        const roleRecord = user.user_roles.find((r) => r.role === role && !r.revoked_at);
        if (roleRecord) {
          const { error } = await supabase
            .from("user_roles")
            .update({
              revoked_at: new Date().toISOString(),
              revoked_by: currentUser?.id,
            })
            .eq("id", roleRecord.id);

          if (error) throw error;

          // Log role revocation
          if (currentUser) {
            logRoleChange(
              currentUser.id,
              user.id,
              user.full_name || user.email || "Onbekend",
              role,
              "revoke"
            );
          }
        }
      }

      // Refresh user data
      await fetchUser();
    } catch (error) {
      console.error("Error toggling role:", error);
      Alert.alert("Fout", "Kon rol niet wijzigen");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Toggle a role for the user with confirmation
   * Note: Super admins can modify their own familie_admin and user roles,
   * but cannot modify their own super_admin role
   */
  const handleToggleRole = (role: UserRoleType, enabled: boolean) => {
    if (!user) return;
    
    // Prevent self-modification of super_admin role
    if (isCurrentUser && role === "super_admin") return;

    const roleLabel = getRoleLabel(role);
    const userName = user.full_name || user.email;
    const action = enabled ? "toekennen aan" : "intrekken van";
    const actionVerb = enabled ? "Toekennen" : "Intrekken";

    Alert.alert(
      `Rol ${enabled ? "Toekennen" : "Intrekken"}`,
      `Weet je zeker dat je de rol "${roleLabel}" wilt ${action} ${userName}?`,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: actionVerb,
          style: enabled ? "default" : "destructive",
          onPress: () => executeRoleChange(role, enabled),
        },
      ]
    );
  };

  /**
   * Handle password reset request
   */
  const handleResetPassword = () => {
    if (!user) return;

    Alert.alert(
      "Wachtwoord Resetten",
      `Weet je zeker dat je een wachtwoord reset email wilt sturen naar ${user.email}?`,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Verstuur",
          onPress: async () => {
            try {
              // This uses Supabase's built-in password reset
              // Note: For production, you might want to use an Edge Function
              const { error } = await supabase.auth.resetPasswordForEmail(user.email!, {
                redirectTo: "staatsen://auth/callback?type=recovery",
              });

              if (error) throw error;

              Alert.alert("Succes", `Wachtwoord reset email is verstuurd naar ${user.email}`);
            } catch (error) {
              console.error("Error resetting password:", error);
              Alert.alert("Fout", "Kon wachtwoord reset email niet versturen");
            }
          },
        },
      ]
    );
  };

  /**
   * Call the admin-user-actions Edge Function
   */
  const invokeAdminAction = async (
    action: "block_user" | "unblock_user" | "delete_user"
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (!accessToken) {
        return { success: false, error: "Niet ingelogd" };
      }

      const response = await supabase.functions.invoke("admin-user-actions", {
        body: { action, user_id: id },
      });

      if (response.error) {
        return { success: false, error: response.error.message };
      }

      return response.data;
    } catch (error) {
      console.error("Admin action error:", error);
      return { success: false, error: "Er is een fout opgetreden" };
    }
  };

  /**
   * Handle blocking user via Edge Function
   */
  const handleBlockUser = () => {
    if (!user || isCurrentUser) return;

    Alert.alert(
      "Gebruiker Blokkeren",
      `Weet je zeker dat je ${user.full_name || user.email} wilt blokkeren? Deze gebruiker kan dan niet meer inloggen.`,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Blokkeren",
          style: "destructive",
          onPress: async () => {
            setIsSaving(true);
            const result = await invokeAdminAction("block_user");
            setIsSaving(false);

            if (result.success) {
              // Log user block action
              if (currentUser) {
                logUserAction(currentUser.id, user.id, user.full_name || user.email || "Onbekend", "block");
              }
              Alert.alert("Succes", "Gebruiker is geblokkeerd");
            } else {
              Alert.alert("Fout", result.error || "Kon gebruiker niet blokkeren");
            }
          },
        },
      ]
    );
  };

  /**
   * Handle unblocking user via Edge Function
   */
  const handleUnblockUser = () => {
    if (!user || isCurrentUser) return;

    Alert.alert(
      "Gebruiker Deblokkeren",
      `Weet je zeker dat je ${user.full_name || user.email} wilt deblokkeren?`,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Deblokkeren",
          onPress: async () => {
            setIsSaving(true);
            const result = await invokeAdminAction("unblock_user");
            setIsSaving(false);

            if (result.success) {
              // Log user unblock action
              if (currentUser) {
                logUserAction(currentUser.id, user.id, user.full_name || user.email || "Onbekend", "unblock");
              }
              Alert.alert("Succes", "Gebruiker is gedeblokkeerd");
            } else {
              Alert.alert("Fout", result.error || "Kon gebruiker niet deblokkeren");
            }
          },
        },
      ]
    );
  };

  /**
   * Handle deleting user via anonymize function
   * Uses soft-delete approach: anonymizes profile and blocks access
   */
  const handleDeleteUser = () => {
    if (!user || isCurrentUser) return;

    const userName = user.full_name || user.email || "deze gebruiker";

    Alert.alert(
      "Gebruiker Verwijderen",
      `Weet je zeker dat je ${userName} wilt verwijderen?\n\n` +
        "⚠️ Deze actie kan niet ongedaan worden gemaakt.\n\n" +
        "Het profiel wordt geanonimiseerd en de gebruiker kan niet meer inloggen. " +
        "Berichten in chats blijven zichtbaar als 'Verwijderd Gebruiker'.",
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            setIsSaving(true);
            try {
              // Call the anonymize function
              const { data, error } = await supabase.rpc("anonymize_user_account", {
                p_user_id: user.id,
                p_reason: "admin_deleted",
              });

              if (error) throw error;

              Alert.alert("Succes", `${userName} is verwijderd`, [
                { text: "OK", onPress: () => router.back() },
              ]);
            } catch (error: any) {
              console.error("Error deleting user:", error);
              Alert.alert("Fout", error.message || "Kon gebruiker niet verwijderen");
            } finally {
              setIsSaving(false);
            }
          },
        },
      ]
    );
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color="#DC2626" />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>
          Gebruiker laden...
        </Text>
      </SafeAreaView>
    );
  }

  if (!user) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <Ionicons name="person-outline" size={48} color={colors.textTertiary} />
        <Text className="mt-2" style={{ color: colors.textTertiary }}>
          Gebruiker niet gevonden
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        {/* User Header */}
        <View
          className="px-4 py-6 items-center"
          style={{
            backgroundColor: colors.surface,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
          }}
        >
          <View
            className="w-20 h-20 rounded-full items-center justify-center mb-3"
            style={{ backgroundColor: user.is_dependent ? "#DBEAFE" : "#7C3AED20" }}
          >
            <Text className="font-bold text-3xl" style={{ color: user.is_dependent ? "#1D4ED8" : "#7C3AED" }}>
              {(user.full_name || user.email || "?").charAt(0).toUpperCase()}
            </Text>
          </View>
          <Text className="text-xl font-bold" style={{ color: colors.text }}>
            {user.full_name || "Geen naam"}
          </Text>
          <Text className="mt-1" style={{ color: colors.textSecondary }}>
            {user.is_dependent ? "Geen e-mail (kind)" : user.email}
          </Text>
          {user.nickname && (
            <Text className="text-sm mt-0.5" style={{ color: colors.textTertiary }}>
              "{user.nickname}"
            </Text>
          )}
          {user.is_dependent && (
            <View className="mt-2 px-3 py-1 rounded-full" style={{ backgroundColor: "#DBEAFE" }}>
              <Text style={{ color: "#1D4ED8" }} className="text-sm font-medium">Kind</Text>
            </View>
          )}
          {isCurrentUser && (
            <View className="mt-2 bg-yellow-100 px-3 py-1 rounded-full">
              <Text className="text-yellow-700 text-sm font-medium">Dit ben jij</Text>
            </View>
          )}
        </View>

        {/* User Info */}
        <View className="mt-4">
          <Text
            className="px-4 py-2 text-sm font-semibold uppercase tracking-wide"
            style={{ color: colors.textSecondary }}
          >
            Gebruiker Info
          </Text>
          <View style={{ backgroundColor: colors.surface }}>
            <View
              className="px-4 py-3 flex-row justify-between"
              style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
            >
              <Text style={{ color: colors.textSecondary }}>Email</Text>
              <Text style={{ color: colors.text }}>{user.email}</Text>
            </View>
            <View
              className="px-4 py-3 flex-row justify-between"
              style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
            >
              <Text style={{ color: colors.textSecondary }}>Telefoon</Text>
              <Text style={{ color: colors.text }}>{user.phone || "Niet ingevuld"}</Text>
            </View>
            <View
              className="px-4 py-3 flex-row justify-between"
              style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
            >
              <Text style={{ color: colors.textSecondary }}>Geboortedatum</Text>
              <Text style={{ color: colors.text }}>{user.date_of_birth || "Niet ingevuld"}</Text>
            </View>
            <View className="px-4 py-3 flex-row justify-between">
              <Text style={{ color: colors.textSecondary }}>Aangemaakt</Text>
              <Text style={{ color: colors.text }}>
                {new Date(user.created_at).toLocaleDateString("nl-NL")}
              </Text>
            </View>
          </View>
        </View>

        {/* Role Management - Only for non-dependent users */}
        {!user.is_dependent && (
          <View className="mt-4">
            <Text
              className="px-4 py-2 text-sm font-semibold uppercase tracking-wide"
              style={{ color: colors.textSecondary }}
            >
              Rollen Beheren
            </Text>
            {isCurrentUser && (
              <View className="px-4 py-2" style={{ backgroundColor: "#FEF3C7" }}>
                <Text className="text-sm" style={{ color: "#92400E" }}>
                  Je kunt je eigen Super Admin rol niet wijzigen
                </Text>
              </View>
            )}
            <RoleToggle
              role="super_admin"
              label="Super Admin"
              description="Volledige toegang tot alle functies"
              isActive={hasRole("super_admin")}
              onToggle={(enabled) => handleToggleRole("super_admin", enabled)}
              disabled={isCurrentUser || isSaving}
              colors={colors}
            />
            <RoleToggle
              role="familie_admin"
              label="Familie Admin"
              description="Kan weekend activiteiten beheren"
              isActive={hasRole("familie_admin")}
              onToggle={(enabled) => handleToggleRole("familie_admin", enabled)}
              disabled={isSaving}
              colors={colors}
            />
            <RoleToggle
              role="user"
              label="Gebruiker"
              description="Standaard rol voor alle familieleden"
              isActive={hasRole("user")}
              onToggle={(enabled) => handleToggleRole("user", enabled)}
              disabled={isSaving}
              colors={colors}
            />
          </View>
        )}

        {/* Guardian Info - Only for dependent users */}
        {user.is_dependent && (
          <View className="mt-4">
            <Text
              className="px-4 py-2 text-sm font-semibold uppercase tracking-wide"
              style={{ color: colors.textSecondary }}
            >
              Kind Informatie
            </Text>
            <View style={{ backgroundColor: colors.surface }}>
              <View
                className="px-4 py-3 flex-row justify-between"
                style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
              >
                <Text style={{ color: colors.textSecondary }}>Type</Text>
                <Text style={{ color: colors.text }}>Kind (geen eigen account)</Text>
              </View>
              <View
                className="px-4 py-3 flex-row justify-between"
                style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
              >
                <Text style={{ color: colors.textSecondary }}>Guardians</Text>
                <Text style={{ color: colors.text }}>
                  {user.guardian_ids && user.guardian_ids.length > 0
                    ? `${user.guardian_ids.length} guardian(en)`
                    : "Geen guardians toegewezen"}
                </Text>
              </View>
              <View className="px-4 py-3">
                <Text className="text-sm" style={{ color: colors.textTertiary }}>
                  Guardians kunnen worden toegewezen door een Familie Admin bij het team beheer.
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* Admin Actions */}
        <View className="mt-4">
          <Text
            className="px-4 py-2 text-sm font-semibold uppercase tracking-wide"
            style={{ color: colors.textSecondary }}
          >
            Acties
          </Text>
          {user.is_dependent ? (
            <>
              {/* Merge action for dependent users */}
              <ActionButton
                icon="git-merge-outline"
                title="Samenvoegen met Account"
                description="Voeg dit kind samen met een bestaand account"
                onPress={() => router.push(`/(super-admin)/users/merge?dependentId=${user.id}`)}
                colors={colors}
              />
              <ActionButton
                icon="trash-outline"
                title="Kind Verwijderen"
                description="Permanent verwijderen van de app"
                onPress={handleDeleteUser}
                variant="danger"
                colors={colors}
              />
            </>
          ) : (
            <>
              <ActionButton
                icon="link-outline"
                title="Koppelen aan Pre-registratie"
                description="Koppel deze gebruiker aan een bestaande pre-registratie"
                onPress={handleShowLinkModal}
                colors={colors}
              />
              <ActionButton
                icon="mail-outline"
                title="Wachtwoord Reset Versturen"
                description="Stuur een reset link naar de gebruiker"
                onPress={handleResetPassword}
                colors={colors}
              />
              <ActionButton
                icon="ban-outline"
                title="Gebruiker Blokkeren"
                description="Blokkeer toegang tot de app"
                onPress={handleBlockUser}
                variant="danger"
                disabled={isCurrentUser}
                colors={colors}
              />
              <ActionButton
                icon="checkmark-circle-outline"
                title="Gebruiker Deblokkeren"
                description="Herstel toegang tot de app"
                onPress={handleUnblockUser}
                disabled={isCurrentUser}
                colors={colors}
              />
              <ActionButton
                icon="trash-outline"
                title="Gebruiker Verwijderen"
                description="Permanent verwijderen van de app"
                onPress={handleDeleteUser}
                variant="danger"
                disabled={isCurrentUser}
                colors={colors}
              />
            </>
          )}
        </View>

        {/* Spacer */}
        <View className="h-8" />
      </ScrollView>

      {/* Saving Indicator */}
      {isSaving && (
        <View className="absolute inset-0 bg-black/20 items-center justify-center">
          <View
            className="rounded-xl px-6 py-4 flex-row items-center"
            style={{ backgroundColor: colors.surface }}
          >
            <ActivityIndicator size="small" color="#DC2626" />
            <Text className="ml-3" style={{ color: colors.text }}>
              Opslaan...
            </Text>
          </View>
        </View>
      )}

      {/* Link to Pending Member Modal */}
      <Modal
        visible={showLinkModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowLinkModal(false)}
      >
        <View className="flex-1" style={{ backgroundColor: colors.background }}>
          {/* Header */}
          <View
            className="flex-row items-center justify-between px-4 py-4 border-b"
            style={{ borderBottomColor: colors.border }}
          >
            <TouchableOpacity onPress={() => setShowLinkModal(false)}>
              <Text style={{ color: colors.primary }}>Annuleren</Text>
            </TouchableOpacity>
            <Text className="text-lg font-semibold" style={{ color: colors.text }}>
              Koppel aan Pre-registratie
            </Text>
            <View style={{ width: 70 }} />
          </View>

          {/* Content */}
          {isLoadingPending ? (
            <View className="flex-1 items-center justify-center">
              <ActivityIndicator size="large" color="#DC2626" />
            </View>
          ) : unclaimedPendingMembers.length === 0 ? (
            <View className="flex-1 items-center justify-center px-8">
              <Ionicons name="people-outline" size={48} color={colors.textSecondary} />
              <Text className="text-lg mt-4 text-center" style={{ color: colors.text }}>
                Geen open pre-registraties
              </Text>
              <Text className="mt-2 text-center" style={{ color: colors.textSecondary }}>
                Alle pre-registraties zijn al gekoppeld aan een gebruiker.
              </Text>
            </View>
          ) : (
            <ScrollView className="flex-1 px-4 py-4">
              <Text className="text-sm mb-4" style={{ color: colors.textSecondary }}>
                Selecteer een pre-registratie om te koppelen aan {user?.full_name || user?.email}:
              </Text>
              {unclaimedPendingMembers.map((pending) => (
                <TouchableOpacity
                  key={pending.id}
                  onPress={() => handleLinkToPendingMember(pending.id)}
                  disabled={isLinking}
                  className="rounded-xl p-4 mb-3"
                  style={{
                    backgroundColor: colors.surface,
                    opacity: isLinking ? 0.5 : 1,
                  }}
                >
                  <View className="flex-row items-center">
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{ backgroundColor: "#F5920520" }}
                    >
                      <Ionicons name="person-outline" size={20} color="#F59205" />
                    </View>
                    <View className="flex-1">
                      <Text className="font-semibold" style={{ color: colors.text }}>
                        {pending.full_name}
                      </Text>
                      <Text className="text-sm" style={{ color: colors.textSecondary }}>
                        {pending.email}
                      </Text>
                      {pending.team && (
                        <View className="flex-row items-center mt-1">
                          <View
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: pending.team.color }}
                          />
                          <Text className="text-xs" style={{ color: colors.textSecondary }}>
                            {pending.team.name}
                          </Text>
                        </View>
                      )}
                    </View>
                    <Ionicons name="chevron-forward" size={20} color={colors.textSecondary} />
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}

          {/* Linking Indicator */}
          {isLinking && (
            <View className="absolute inset-0 bg-black/20 items-center justify-center">
              <View
                className="rounded-xl px-6 py-4 flex-row items-center"
                style={{ backgroundColor: colors.surface }}
              >
                <ActivityIndicator size="small" color="#DC2626" />
                <Text className="ml-3" style={{ color: colors.text }}>
                  Koppelen...
                </Text>
              </View>
            </View>
          )}
        </View>
      </Modal>
    </SafeAreaView>
  );
}
